import { KeyboardOutlined, OpenInNewOutlined } from '@mui/icons-material'
import { Box, Chip, Grid, Stack, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useTranslations } from 'next-intl'
import { type ReactNode } from 'react'

import Link from '~/components/Link'
import { Overlay, createOverlay } from '~/components/Overlay'
import { activeBackend, allowsRuntimeBackendSwitch } from '~/configs/env'
import useIsMac from '~/hooks/useIsMac'

import { type KeyCombo, comboToString } from './comboUtils'
import { DOCS_URL } from './constants'
import { usePageShortcutsRegistration } from './PageShortcutsContext'
import { useGShortcutRoutes } from './useShortcutRoutes'

const Kbd = styled('kbd')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 24,
  padding: `${theme.spacing(0.5)} ${theme.spacing(1.25)}`,
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  color: theme.palette.text.primary,
  fontFamily: 'monospace',
  fontSize: '0.75rem',
  lineHeight: 1
}))

const Separator = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.75rem',
  margin: `0 ${theme.spacing(0.75)}`
}))

const SectionTitle = styled('h3')(({ theme }) => ({
  fontWeight: 600,
  fontSize: '0.75rem',
  letterSpacing: '0.04em',
  color: theme.palette.text.secondary,
  margin: 0,
  marginBottom: theme.spacing(0.5)
}))

const SectionSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(0.5)
}))

const Row = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  padding: `${theme.spacing(1.25)} 0`,
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:last-of-type': {
    borderBottom: 'none'
  }
}))

const HeaderIconBox = styled(Box)(({ theme }) => ({
  width: 36,
  height: 36,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.action.hover,
  color: theme.palette.primary.main,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0
}))

const PagePill = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.accent.light,
  color: theme.palette.common.white,
  fontWeight: 500,
  fontSize: '0.75rem',
  height: 20,
  borderRadius: 10,
  '& .MuiChip-label': {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1)
  }
}))

const Footer = styled(Box)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.action.hover,
  padding: `${theme.spacing(1.5)} ${theme.spacing(3)}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2)
}))

type RowProps = {
  label: ReactNode
  combo: ReactNode
  indicator?: ReactNode
}

const ShortcutRow = ({ label, combo, indicator }: RowProps) => (
  <Row>
    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
      {indicator}
      {/* `component="div"` so labels can contain block-level children (e.g. the docs row's
          inline link Stack) without producing invalid `<div>` inside `<p>` markup. */}
      <Typography component="div" variant="body2">
        {label}
      </Typography>
    </Stack>
    <Stack direction="row" alignItems="center" spacing={0} sx={{ flexShrink: 0 }}>
      {combo}
    </Stack>
  </Row>
)

/**
 * Renders a list of keys with a separator between them. `kind="sequence"` uses the
 * localized "then" word (G then D); `kind="chord"` uses a `+` (Shift + D, Cmd + K).
 */
const Combo = ({ keys, kind }: { keys: ReactNode[]; kind: 'sequence' | 'chord' }) => {
  const t = useTranslations('components.KeyboardShortcutsOverlay')
  const separator = kind === 'sequence' ? t('separators_then') : '+'
  return (
    <>
      {keys.map((k, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <Box component="span" key={i} sx={{ display: 'inline-flex', alignItems: 'center' }}>
          {i > 0 && <Separator>{separator}</Separator>}
          <Kbd>{k}</Kbd>
        </Box>
      ))}
    </>
  )
}

const Section = ({
  title,
  subtitle,
  children
}: {
  title: string
  subtitle?: string
  children: ReactNode
}) => (
  <Box sx={{ mb: 4, '&:last-of-type': { mb: 0 } }}>
    <SectionTitle>{title}</SectionTitle>
    {subtitle && <SectionSubtitle>{subtitle}</SectionSubtitle>}
    <Box sx={{ mt: 1 }}>{children}</Box>
  </Box>
)

/**
 * Render a page-shortcut combo. Single-letter keys are uppercased for visual consistency
 * with the rest of the overlay (G, D, R) — digits and symbols pass through unchanged.
 * `shiftLabel` is passed in so the rendered "Shift" matches the platform-aware label
 * the rest of the overlay uses (⇧ on Mac, "Shift" elsewhere).
 */
const renderPageCombo = (combo: KeyCombo, shiftLabel: string): ReactNode => {
  const display = combo.key.length === 1 && /[a-z]/i.test(combo.key) ? combo.key.toUpperCase() : combo.key
  if (combo.hasShift) return <Combo keys={[shiftLabel, display]} kind="chord" />
  return <Kbd>{display}</Kbd>
}

const KeyboardShortcutsOverlay = createOverlay(() => {
  const t = useTranslations('components.KeyboardShortcutsOverlay')
  const pageRegistration = usePageShortcutsRegistration()
  const isMac = useIsMac()

  // Mac keyboards label modifier keys with Unicode glyphs; non-Mac platforms use the
  // English word. Sidesteps localization (the keycap label matches what's on the user's
  // keyboard regardless of UI language).
  const shiftLabel = isMac ? '⇧' : 'Shift'
  const cmdLabel = isMac ? '⌘' : 'Ctrl'
  const escLabel = isMac ? '⎋' : 'Esc'

  // The "ON {PAGE}" pill only appears when the current page has registered page-specific
  // shortcuts — promising context with nothing to back it up is misleading. The total
  // shortcut count is still shown on every page since it's a useful global stat.
  const pageLabel = pageRegistration?.pageLabel ?? null

  // All rows share the same `{ key, label, combo }` shape so the JSX render and the
  // header count derive from the same source — no hardcoded magic numbers, no separate
  // typing for navigation rows.
  type ShortcutRowData = { key: string; label: ReactNode; combo: ReactNode }
  // Source the G-leader navigation rows from `useGShortcutRoutes` (R3.3) so
  // the overlay only advertises shortcuts the current user can actually
  // reach. The hook applies both JS-conditional gates (e.g. feature flags)
  // and CASL ACL filters via the user's flattened sidebar nav. Same source
  // `useKeyboardShortcuts` uses for the indicator follow-ups — single
  // source of truth, no drift between overlay and indicator.
  const gShortcutRoutes = useGShortcutRoutes()
  const navigationRows: ShortcutRowData[] = gShortcutRoutes.map(({ displayKey, label, route }) => ({
    key: route, // unique React key; route is guaranteed unique per spec
    label,
    combo: <Combo keys={['G', displayKey]} kind="sequence" />
  }))

  const quickActionRows: ShortcutRowData[] = [
    { key: 'quickSearch', label: t('actions_quickSearch'), combo: <Combo keys={[cmdLabel, 'K']} kind="chord" /> },
    { key: 'notifications', label: t('actions_notifications'), combo: <Kbd>N</Kbd> }
  ]
  const viewControlRows: ShortcutRowData[] = [
    { key: 'toggleSidebar', label: t('actions_toggleSidebar'), combo: <Kbd>.</Kbd> }
  ]
  const universalRows: ShortcutRowData[] = [
    { key: 'close', label: t('actions_close'), combo: <Kbd>{escLabel}</Kbd> }
  ]
  // Backend-switcher rows are preview-only — same gate as the existing `BackendSwitcher`
  // Chip UI. Hidden in gamma/prod/prod_eu builds where `switchBackend` is a no-op.
  // Same `B then <key>` shape as the navigation `G then <key>` rows above. The row
  // matching `activeBackend` gets a small "(current)" marker so users can see at a
  // glance which backend they're already on (avoids accidentally re-switching to it).
  const withCurrentMarker = (label: string, isCurrent: boolean): ReactNode =>
    isCurrent ? (
      <>
        {label}{' '}
        <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
          ({t('backend_current')})
        </Box>
      </>
    ) : (
      label
    )
  const backendRows: ShortcutRowData[] = allowsRuntimeBackendSwitch
    ? [
        { key: 'backend_gamma', label: withCurrentMarker(t('backend_gamma'), activeBackend === 'gamma'), combo: <Combo keys={['B', 'G']} kind="sequence" /> },
        { key: 'backend_prod', label: withCurrentMarker(t('backend_prod'), activeBackend === 'prod'), combo: <Combo keys={['B', 'P']} kind="sequence" /> },
        { key: 'backend_prodEu', label: withCurrentMarker(t('backend_prodEu'), activeBackend === 'prod_eu'), combo: <Combo keys={['B', 'E']} kind="sequence" /> }
      ]
    : []

  const helpRows: ShortcutRowData[] = [
    { key: 'help', label: t('actions_help'), combo: <Kbd>?</Kbd> },
    {
      key: 'docs',
      label: (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <span>{t('actions_docs')}</span>
          <Link href={DOCS_URL} target="_blank" rel="noopener noreferrer">
            {/* `~/components/Link` is a styled NextLink that filters `sx` (the styled-components
                `shouldForwardProp` strips it). Wrap children in a Box so the inline-flex layout
                + theme-aware gap go through MUI's spacing scale rather than raw pixels. */}
            <Box
              component="span"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.75rem' }}
            >
              synop.readme.io
              <OpenInNewOutlined sx={{ fontSize: '0.875rem' }} />
            </Box>
          </Link>
        </Stack>
      ),
      combo: <Combo keys={[shiftLabel, 'D']} kind="chord" />
    }
  ]

  const totalShortcuts =
    navigationRows.length +
    quickActionRows.length +
    viewControlRows.length +
    universalRows.length +
    helpRows.length +
    backendRows.length +
    (pageRegistration?.shortcuts.length ?? 0)

  return (
    <Overlay maxWidth="md" fullWidth>
      <Overlay.Header
        prefix={
          <HeaderIconBox>
            <KeyboardOutlined fontSize="small" />
          </HeaderIconBox>
        }
        title={t('title')}
      >
        {pageLabel && <PagePill size="small" variant="filled" label={t('header_onPage', { page: pageLabel.toUpperCase() })} />}
        <Typography variant="caption" color="text.secondary">
          {t('header_shortcutsAvailable', { count: totalShortcuts })}
        </Typography>
      </Overlay.Header>
      <Overlay.Content sx={{ pb: 2 }}>
        <Grid container spacing={6}>
          <Grid item xs={12} md={6}>
            <Section title={t('sections_navigation')} subtitle={t('subtitles_navigation')}>
              {navigationRows.map((row) => (
                <ShortcutRow key={row.key} label={row.label} combo={row.combo} />
              ))}
            </Section>

            {pageRegistration && pageRegistration.shortcuts.length > 0 && (
              <Section
                title={pageRegistration.sectionLabel ?? pageRegistration.pageLabel}
                subtitle={pageRegistration.sectionSubtitle}
              >
                {pageRegistration.shortcuts.map((s) => (
                  <ShortcutRow
                    key={comboToString(s.combo)}
                    label={s.label}
                    indicator={s.indicator}
                    combo={renderPageCombo(s.combo, shiftLabel)}
                  />
                ))}
              </Section>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <Section title={t('sections_quickActions')}>
              {quickActionRows.map((row) => (
                <ShortcutRow key={row.key} label={row.label} combo={row.combo} />
              ))}
            </Section>

            <Section title={t('sections_viewControls')}>
              {viewControlRows.map((row) => (
                <ShortcutRow key={row.key} label={row.label} combo={row.combo} />
              ))}
            </Section>

            <Section title={t('sections_universal')}>
              {universalRows.map((row) => (
                <ShortcutRow key={row.key} label={row.label} combo={row.combo} />
              ))}
            </Section>

            {/* Backend (preview-only) section is rendered before Help so Help stays
                pinned as the last section in the right column regardless of feature flags. */}
            {backendRows.length > 0 && (
              <Section title={t('sections_backend')} subtitle={t('subtitles_backend')}>
                {backendRows.map((row) => (
                  <ShortcutRow key={row.key} label={row.label} combo={row.combo} />
                ))}
              </Section>
            )}

            <Section title={t('sections_help')}>
              {helpRows.map((row) => (
                <ShortcutRow key={row.key} label={row.label} combo={row.combo} />
              ))}
            </Section>
          </Grid>
        </Grid>
      </Overlay.Content>

      <Footer>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Kbd>{escLabel}</Kbd>
          <Typography variant="caption" color="text.secondary">
            {t('footer_close')}
          </Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {t.rich('footer_reopenSentence', { kbd: (chunks) => <Kbd>{chunks}</Kbd> })}
        </Typography>
      </Footer>
    </Overlay>
  )
})

export default KeyboardShortcutsOverlay
